export const TRAINING_GUIDE_MD = `# NavScora Admin — Training Guide

A practical walkthrough of how each part of the platform works. Written for the NavScora team.

---

## 1. Signing in & account security

### Logging in
1. Go to the app and enter your **email + password**.
2. If **two-factor authentication (2FA)** is enabled on your account, you'll be asked for a 6-digit code from your authenticator app after your password.
3. Wrong passwords/codes are rate-limited — after 5 failures the account locks for ~15 minutes.

### Two-factor authentication (Settings → Security)
- **Enable 2FA:** click *Enable 2FA*, scan the QR code with Google Authenticator / Authy / 1Password, enter the 6-digit code to confirm.
- **Recovery codes:** the moment 2FA is enabled you're shown **10 one-time recovery codes**.
  - Use one instead of your authenticator if you lose your phone.
  - Each works **once**. **Copy** and **Download** them, tick *"I have securely saved…"*, then Done.
  - NavScora **cannot** show them again. You can see how many remain in Settings → Security.
  - *Generate new recovery codes* (needs your password + a current code) replaces the whole set.
- **Using a recovery code at login:** on the code screen click *"Use a recovery code"* and paste one.
- **Disabling 2FA** needs your password **and** a current authenticator or recovery code.

### Passwords & sessions
- Changing your password, disabling 2FA, or regenerating recovery codes **signs out your other sessions** automatically (your current one stays in).
- A password reset never disables 2FA — you still need your authenticator/recovery code to log in.

---

## 2. Navigation & roles

The left sidebar groups everything: **Overview**, **Finance**, **Legal & Contracts**, plus Customers, Products, Usage, Integrations, Reports and Settings.

**Roles** (who can do what) are managed in **Legal → Settings → Team & Roles** (super-admin only):
- **super_admin** — full access, including legal approval and user management.
- **legal_admin** — manage templates, contracts, reviews, generate documents (cannot *approve* legal wording).
- **legal_approver** — can record legal approval.
- Other roles (finance_admin, accountant, product_manager, auditor, support_manager, read_only) follow least-privilege.

> Tip: create real \`legal_approver\` accounts for your lawyer/reviewer instead of doing everything as super-admin.

---

## 3. Legal & Contracts

Think of the flow as: **Template → (Legal review → Approval) → Contract → Generate document → Sign → Executed → Renewal tracking.**

### Dashboard
Shows counts (active/draft contracts, awaiting signature, reviews due/overdue, templates needing review), an **Action Required** list, and quick-action buttons.

### Templates (the master wording)
- The library ships with the **NavScora Master SaaS Agreement v1.0** + Schedules 1–5, all at status **"Legal Review Required"** (a controlled draft — *not* lawyer-approved yet).
- **Create Template:** name, category, jurisdiction, and body written in the **WYSIWYG editor** (headings, bold, lists). Put merge fields in double braces, e.g. \`{{customer_legal_name}}\`.
- **Versions are immutable.** Editing wording creates a **new version** (change notes required); the old version is preserved forever. New wording flips the template back to "Legal Review Required".
- **Preview PDF / DOCX** and per-version downloads are available on the template page.

### Legal review workflow
1. On a template, click **Send for Legal Review** — record the reviewer (internal/external), organisation, due date and instructions. This never changes the underlying version.
2. Track status (Sent → Under Review → Changes Requested → Approved, etc.) on the **Legal Review** page.
3. **Upload returned** lawyer files against the review. Uploading a file does **not** approve anything.
4. **Record Legal Approval** (super_admin / legal_approver only) is an explicit action that marks a specific version Approved. Review copies are watermarked *"DRAFT — FOR LEGAL REVIEW — NOT FOR EXECUTION"*; approved versions drop the watermark.

### Contracts
- **New Contract:** pick an **existing customer** + product + type + base template. Gets an auto reference (NS-C-0001). It references your real customer record — no duplicate data.
- On the contract page: edit commercial terms, dates, notice period (the **non-renewal notice deadline** is auto-calculated = expiry − notice days), and add negotiation notes.
- **Generate Draft** renders the linked template with the customer's details; any field it can't fill is flagged as \`[[field]]\` so nothing is silently invented.
- **Lifecycle:** Draft → Internal Review → Sent → Negotiation → Awaiting Signature → Executed → Active…

### Executed documents (immutable)
- **Upload Signed & Execute** on a contract: attach the signed PDF/DOCX, list signatories and dates. The system stores a **SHA-256 checksum** and **locks the contract** — it can no longer be edited or deleted (corrections go through amendments/variations).
- The **Executed Documents** page is the audit-grade repository of every signed agreement.

### Document Generator
Guided screen: Customer → Product → Template → **Preview** (auto-filled, missing fields flagged) → **Open PDF** / **Download DOCX**.

### Legal Register & Alerts
- **Legal Register:** one searchable/filterable table of every contract (status, dates, value, owner).
- **Alerts & Renewals:** legal-review reminders (due/overdue) and contract renewal / notice-deadline warnings.

### Customer page → Contracts & Legal
Each customer record shows that customer's contracts and status inline.

---

## 4. Invoices (Accounts Receivable)

- **Invoices** is a separate Accounts Receivable ledger — it never touches the Finance revenue figures, so nothing is double-counted.
- **New Invoice:** pick a customer, add line items (each item can be **GST-inclusive or GST-exclusive**). NavScora computes subtotal, GST and total using exact integer-cents math — no floating-point rounding drift.
- Every invoice gets a unique, sequential number: **NS-INV-YEAR-####**.
- **Statuses:** Draft → Issued → Paid. Record part or full **payments** against an issued invoice.
- **Download PDF** produces an Australian GST-compliant tax invoice.

---

## 5. Everyday tips
- **Product separation:** MarineOS and RCF Runsheet are deliberately separate — RCF is never nested under MarineOS.
- **Audit trail:** every significant legal/user/invoice action is recorded (who, when, what changed).
- **Nothing is hard-deleted** for legal/financial records — items are archived/voided for audit.
`;
